import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import { transformResponseHandler } from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const brandApi = createApi({
  reducerPath: REDUCER_PATHS.BRAND,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    brandListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRANDS_LIST,
        method: "POST",
        body: data,
      }),
    }),
    addBrands: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_BRANDS,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    brandFileUplaod: builder.mutation({
      query: (data) => ({
        url: apiConfig.UPLOAD_BRAND_IMAGES,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changeBrandStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHANGE_BRAND,
        body: data,
        method: "PATCH",
      }),
      // invalidatesTags: ["clubDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    brandDetailsDownload: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.DOWNLOAD_BRAND}/${data.type}?filter=${data.filter}&column=${data.column}`,
        method: "GET",
      }),
    }),
    deleteBrand: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_BRAND,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editBrandProfile: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_BRAND + "/" + data?.id,
        method: "PUT",
        body: {
          brandFullName: data?.brandFullName,
          brandShortName: data?.brandShortName,
          description: data?.description,
          contactNumber: data?.contactNumber,
          redemptionMethod: data?.redemptionMethod,
          thumbnailImage: data?.thumbnailImage,
          coverImage: data?.coverImage,
          coverImageHorizontal: data?.coverImageHorizontal,
          otherImages: data?.otherImages,
          websiteUrls: data?.websiteUrls,
          videoLinks: data?.videoLinks,
          status: data?.status,
          drinkTypes: data?.drinkTypes,
          isPublished: data?.isPublished,
        },
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getBrandProfileDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_PROFILE_DETAILS + "/" + data?.id,
        method: "GET",
      }),
    }),
    // surveys
    deleteBrandSurveys: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_BRAND_SURVEY + "/" + data?.id,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addBrandSurveys: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_BRAND_SURVEY,
        body: data,
        method: "post",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editBrandSurveys: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_BRAND_SURVEY,
        body: data,
        method: "post",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    brandSurveyListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_SURVEY_LIST,
        method: "POST",
        body: data,
      }),
    }),
    getBrandSurveyDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.GET_BRAND_SURVEY_DETAILS + "/" + data?.id,
        method: "GET",
      }),
    }),
    brandTheBest: builder.mutation({
      query: (data) => ({
        url: apiConfig.MARK_VERY_BEST,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    brandAdminsList: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_ADMINS_LIST,
        method: "POST",
        body: data,
      }),
    }),
    brandActionPointsListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_ACTION_POINTS_LISTING + data.id,
        method: "GET",
      }),
    }),
    saveBrandBenefits: builder.mutation({
      query: (data) => ({
        url: apiConfig.SAVE_BRAND_BENEFITS,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    benefitsFileUpload: builder.mutation({
      query: (data) => ({
        url: apiConfig.FILE_UPLOAD_BRAND_BENEFITS,
        method: "POST",
        body: data,
      }),
    }),
    brandBenefitsMapping: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_BENEFITS_MAPPING,
        method: "POST",
        body: data,
      }),
    }),
    brandBenefitsListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_BENEFITS_LISTING + data?.id,
        method: "GET",
      }),
    }),
    savedBrandBenefitsListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_SAVED_BENEFITS_LIST + data?.id,
        method: "GET",
      }),
    }),
    brandNameDropdown: builder.mutation({
      query: () => ({
        url: apiConfig.BRAND_NAME_DROPDOWN,
        method: "GET",
      }),
    }),
    increaseTagBrandCount: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_INCREASE_TAG_COUNT,
        method: "POST",
        body: data,
      }),
    }),
    decreaseTagBrandCount: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_DECREASE_TAG_COUNT,
        method: "POST",
        body: data,
      }),
    }),
    getBrandUserStats: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_USERS_STATES + "/" + data?.id,
        method: "GET",
      }),
    }),
    getBrandTopUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_TOP_USERS + "/" + data?.id + "/" + data.userCount,
        method: "GET",
      }),
    }),
    getBrandLevelWiseUser: builder.mutation({
      query: (data) => ({
        url:
          apiConfig.BRAND_LEVEL_WISE_USERS + "/" + data?.id + "/" + data.level,
        method: "GET",
      }),
    }),
    brandAdminBrandNameListing: builder.mutation({
      query: () => ({
        url: apiConfig.BRANDADMIN_BRAND_NAME_LIST,
        method: "GET",
      }),
    }),

    brandSubscribersList: builder.mutation({
      query: (data) => ({
        url: apiConfig.BRAND_SUBSCRIBER_LIST,
        body: data,
        method: "POST",
      }),
    }),
    brandSubscribersListDownload: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.BRAND_SUBSCRIBER_LIST_DOWNLOAD}/${data.type}?filter=${data.filter}&column=${data.column}`,
        method: "GET",
      }),
    }),
    getClubLink: builder.mutation({
      query: (data) => ({
        url: apiConfig.CLUB_LINK_LIST,
        method: "POST",
        body: data,
      }),
    }),
    deleteLinkedClub: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_CLUB_LINK_LIST,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getBrandCoOwnerEmail: builder.mutation({
      query: (data) => ({
        url: apiConfig.GET_NEW_BRAND_CO_OWNER_EMAIL + "/" + data,
        method: "GET",
      }),
    }),
    addBrandNewCoOwner: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_NEW_BRAND_CO_OWNER_EMAIL,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteBrandCoOwner: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_BRAND_CO_OWNER + "/" + data?.id,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
  }),
});

export const {
  useBrandSubscribersListMutation,
  useBrandAdminBrandNameListingMutation,

  useGetBrandTopUserMutation,
  useGetBrandLevelWiseUserMutation,
  useGetBrandUserStatsMutation,

  useIncreaseTagBrandCountMutation,
  useDecreaseTagBrandCountMutation,
  useBrandNameDropdownMutation,
  useSavedBrandBenefitsListingMutation,
  useBrandBenefitsListingMutation,
  useBrandBenefitsMappingMutation,
  useBenefitsFileUploadMutation,
  useSaveBrandBenefitsMutation,
  useBrandActionPointsListingMutation,
  useBrandAdminsListMutation,
  useBrandTheBestMutation,
  useGetBrandSurveyDetailsMutation,
  useBrandSurveyListingMutation,
  useAddBrandSurveysMutation,
  useEditBrandSurveysMutation,
  useDeleteBrandSurveysMutation,
  useGetBrandProfileDetailsMutation,
  useEditBrandProfileMutation,
  useDeleteBrandMutation,
  useBrandDetailsDownloadMutation,
  useChangeBrandStatusMutation,
  useBrandFileUplaodMutation,
  useBrandListingMutation,
  useAddBrandsMutation,
  useBrandSubscribersListDownloadMutation,
  useGetClubLinkMutation,
  useDeleteLinkedClubMutation,
  useGetBrandCoOwnerEmailMutation,
  useAddBrandNewCoOwnerMutation,
  useDeleteBrandCoOwnerMutation,
} = brandApi;

export default brandApi;
